import useMediaQuery from '@mui/material/useMediaQuery'
import { StringParam } from 'use-query-params'
import { useState, useEffect, useMemo } from 'react'
import { useQueryParams } from 'use-query-params'
import { debounce } from 'lodash-es'
import Container from '@mui/material/Container'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import Button from '@mui/material/Button'
import Badge from '@mui/material/Badge'
import IconButton from '@mui/material/IconButton'
import Divider from '@mui/material/Divider'
import Drawer from '@mui/material/Drawer'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import List from '@mui/material/List'
import ListItemButton from '@mui/material/ListItemButton'
import ListItemText from '@mui/material/ListItemText'
import Collapse from '@mui/material/Collapse'
import Typography from '@mui/material/Typography'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import SearchRounded from '@mui/icons-material/SearchRounded'
import ClearRounded from '@mui/icons-material/ClearRounded'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import Fade from '@mui/material/Fade'
import { ClickAwayListener, styled, useTheme } from '@mui/material'
import FilterPopper from 'components/FilterPopper/FilterPopper'
import FilterTag from 'components/FilterTag/FilterTag'
import CollapseFilterItem from 'components/CollapseFilterItem/CollapseFilterItem'
import { mapQueryParamsType } from 'utils/query/mapQueryParamsType'
import useAppBarHeight from 'hooks/useAppBarHeight'

const noop = () => undefined

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isMobile',
})(({ theme, isMobile }) => ({
  height: isMobile ? '5.6rem' : '3.2rem',
  width: isMobile ? '100%' : 'fit-content',
  padding: '0.8rem 1.6rem',
  borderRadius: '0.8rem',
  borderColor: theme.vars.palette.buttonBorder,
  color: theme.vars.palette.text.primary,
  ...theme.typography.button,
}))

const createFilterGroups = (filters) => {
  return filters.all.reduce(
    (acc, item) => {
      const { groupId } = item;

      if (groupId) {
        const group = filters.groups.find((g) => g.id === groupId);
        const key = group?.label || groupId;

        if (!acc.groups[key]) {
          acc.groups[key] = [];
        }
        acc.groups[key].push(item);
      } else {
        acc.items.push(item);
      }

      return acc;
    },
    { items: [], groups: {} }
  );
};

function Filter({ filters = {} }) {
  const theme = useTheme()
  const appBarHeight = useAppBarHeight()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [menuAnchorEl, setMenuAnchorEl] = useState(noop)
  const [subMenuAnchorEl, setSubMenuAnchorEl] = useState(noop)
  const [currentFilter, setCurrentFilter] = useState(null)
  const [activeFilters] = useState(filters.active)
  const [query, setQuery] = useQueryParams({
    ...mapQueryParamsType(filters.all),
    search: StringParam
  })
  const [drawerQuery, setDrawerQuery] = useState({ ...query })
  const [searchValue, setSearchValue] = useState(query.search || '')
  const [expanded, setExpand] = useState(
    new Array(filters.groups.length).fill(false)
  )
  const [filteredFilters, setFilteredFilters] = useState(
    createFilterGroups(filters)
  )

  useEffect(() => {
    setFilteredFilters(createFilterGroups(filters))
  }, [filters])

  useEffect(() => {
    if (!query.search && searchValue) setSearchValue('')
  }, [query])

  const handleDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  const handleExpand = (index) => {
    const newExpanded = [...expanded]
    newExpanded[index] = !newExpanded[index]

    if (setSubMenuAnchorEl) {
      handleListItemClick(null)
    }

    setExpand(newExpanded)
  }

  const debouncedSetSearch = useMemo(
    () => debounce((value) => {
      setQuery({
        ...query,
        search: value || undefined // Удаляем параметр если пустая строка
      });
    }, 500),
    [query, setQuery]
  );

  const handleSearchChange = (e) => {
    const value = e.target.value
    setSearchValue(value)
    debouncedSetSearch(value)
  }

  const handleClearSearch = () => {
    setSearchValue('')
    setQuery({ ...query, search: undefined })
  }

  const handleListItemClick = (e, filter) => {
    if (!e || !e.currentTarget) {
      setSubMenuAnchorEl(noop)

      return
    }

    setCurrentFilter(filter)
    setSubMenuAnchorEl(e.currentTarget)
  }

  const handleFilterOpen = (e) => {
    if (isMobile) {
      setIsDrawerOpen(!isDrawerOpen)
    } else {
      menuAnchorEl ? setMenuAnchorEl(noop) : setMenuAnchorEl(e.currentTarget)
    }
  }

  const handleFilterClose = () => {
    setSubMenuAnchorEl(noop)
    setMenuAnchorEl(noop)
  }

  const handleFilterDrawerApply = () => {
    setQuery(drawerQuery)
    handleFilteDrawerClose()
  }

  const handleFilteDrawerClose = () => {
    setIsDrawerOpen(false)
  }

  const handleFilterReset = () => {
    setDrawerQuery({ ...query })
  }

  const handleSearchInputFocus = () => {
    setSubMenuAnchorEl(noop)
  }

  const isPopoverOpen = useMemo(() => {
    return Boolean(menuAnchorEl)
  }, [menuAnchorEl])

  const popoverId = useMemo(() => {
    return isPopoverOpen ? 'filter-popover' : undefined
  }, [isPopoverOpen])

  return (
    <>
      <Stack direction="row" spacing={1.2} sx={{ flexWrap: 'wrap' }} useFlexGap>
        <ClickAwayListener onClickAway={handleFilterClose}>
          <Box sx={{ width: isMobile ? '100%' : 'fit-content' }}>
            <StyledButton
              aria-describedby={popoverId}
              variant="outlined"
              size="small"
              onClick={handleFilterOpen}
              startIcon={!isMobile ? <FilterAltOutlinedIcon /> : null}
              endIcon={
                isMobile ? (
                  <Badge badgeContent={filters.active.length} color="primary">
                    <FilterAltOutlinedIcon />
                  </Badge>
                ) : null
              }
              isMobile={isMobile}
              sx={{
                background: (theme) =>
                  !!menuAnchorEl
                    ? theme.vars.palette.background.paper
                    : 'transparent',
              }}
            >
              Добавить фильтр
            </StyledButton>

            <Popper
              id={popoverId}
              open={isPopoverOpen}
              anchorEl={menuAnchorEl}
              onClose={handleFilterClose}
              placement="bottom-start"
              sx={{ zIndex: 10 }}
              modifiers={[
                {
                  name: 'offset',
                  options: {
                    offset: [0, 8],
                  },
                },
              ]}
              transition
            >
              {({ TransitionProps }) => (
                <>
                  <Fade {...TransitionProps} timeout={200}>
                    <Paper
                      sx={{
                        borderRadius: '0.8rem',
                        minWidth: '37.6rem',
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        sx={{
                          background: theme.vars.palette.filterSearchBg,
                          padding: '0.8rem',
                          overflow: 'hidden',
                        }}
                      >
                        <TextField
                          placeholder="Поиск"
                          size="small"
                          fullWidth
                          sx={{ background: 'red', borderRadius: '0.8rem' }}
                          value={searchValue}
                          onFocus={handleSearchInputFocus}
                          onChange={handleSearchChange}
                          slotProps={{
                            input: {
                              style: {
                                background:
                                theme.vars.palette.background.default,
                              },
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchRounded />
                                </InputAdornment>
                              ),
                              endAdornment: searchValue && (
                                <InputAdornment position="end">
                                  <IconButton
                                    aria-label={'Сбросить'}
                                    onClick={handleClearSearch}
                                  >
                                    <ClearRounded />
                                  </IconButton>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </Box>
                      <List disablePadding>
                        {filters.all.length === 0 ? (
                          <ListItemButton disabled>
                            <ListItemText
                              primary={
                                <Typography variant="buttonLg">
                                  Нет доступных фильтров
                                </Typography>
                              }
                            />
                          </ListItemButton>
                        ) : null}
                        {Object.entries(filteredFilters.groups).map(
                          ([label, items], groupIndex) => (
                            <Box
                              key={groupIndex}
                              sx={{
                                background: theme.vars.palette.background.paper,
                              }}
                            >
                              <ListItemButton
                                key={label + groupIndex}
                                onClick={() => handleExpand(groupIndex)}
                              >
                                <ListItemText
                                  primary={
                                    <Typography variant="buttonLg">
                                      {label}
                                    </Typography>
                                  }
                                />
                                {expanded[groupIndex] ? (
                                  <ExpandLess />
                                ) : (
                                  <ExpandMore />
                                )}
                              </ListItemButton>
                              <Collapse
                                in={expanded[groupIndex]}
                                timeout="auto"
                                unmountOnExit
                              >
                                <List component="div" disablePadding>
                                  {items.length &&
                                    items.map((filter, itemIndex) => (
                                      <ListItemButton
                                        key={itemIndex}
                                        onClick={(e) =>
                                          handleListItemClick(e, filter)
                                        }
                                      >
                                        <ListItemText
                                          sx={{ pl: '20px' }}
                                          primary={filter.label}
                                        />
                                      </ListItemButton>
                                    ))}
                                </List>
                              </Collapse>
                            </Box>
                          )
                        )}
                        {filteredFilters.items.length &&
                        Object.entries(filteredFilters.groups).length ? (
                          <Divider />
                        ) : null}
                        {filteredFilters.items.length ? (
                          <List component="div" disablePadding>
                            {filteredFilters.items.map((filter, itemIndex) => (
                              <ListItemButton
                                sx={{
                                  background:
                                    theme.vars.palette.background.paper,
                                }}
                                key={itemIndex}
                                onClick={(e) => handleListItemClick(e, filter)}
                              >
                                <ListItemText primary={filter.label} />
                              </ListItemButton>
                            ))}
                          </List>
                        ) : null}
                      </List>
                    </Paper>
                  </Fade>

                  <FilterPopper
                    anchorEl={subMenuAnchorEl}
                    filter={currentFilter}
                    query={query}
                    setQuery={setQuery}
                  />
                </>
              )}
            </Popper>
          </Box>
        </ClickAwayListener>

        {filters.active?.length && !isMobile
          ? filters.active.map((filter) => (
              <FilterTag
                key={`${filter.name}-main`}
                filter={filter}
                query={query}
                setQuery={setQuery}
              />
            ))
          : null}
      </Stack>
      <Drawer
        anchor="bottom"
        open={isDrawerOpen}
        onClose={handleDrawerClose}
        PaperProps={{
          sx: {
            maxHeight: `calc(100% - ${appBarHeight}px - 32px)`,
            padding: '1.6rem',
            borderTopRightRadius: '1.6rem',
            borderTopLeftRadius: '1.6rem',
          },
        }}
      >
        <Container>
          <Stack spacing={'2rem'}>
            <Stack
              direction="row"
              sx={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <Typography variant="h3" component="p">
                Фильтр
              </Typography>
              <IconButton
                aria-label={'Закрыть фильтр'}
                onClick={handleFilteDrawerClose}
              >
                <ClearRounded />
              </IconButton>
            </Stack>

            <TextField
              placeholder="Поиск..."
              fullWidth
              value={searchValue}
              onChange={handleSearchChange}
              slotProps={{
                input: {
                  style: {
                    background:
                    theme.vars.palette.background.default,
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRounded />
                    </InputAdornment>
                  ),
                  endAdornment: searchValue && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={'Сбросить'}
                        onClick={handleClearSearch}
                      >
                        <ClearRounded />
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '0.8rem',
                },
              }}
            />

            {activeFilters.length ? (
              <Stack
                direction="row"
                spacing={0.8}
                sx={{ flexWrap: 'wrap' }}
                useFlexGap
              >
                {activeFilters.map((filter) => (
                  <FilterTag
                    key={`${filter.name}-drawer`}
                    filter={filter}
                    query={drawerQuery}
                    setQuery={setDrawerQuery}
                  />
                ))}
              </Stack>
            ) : null}

            <List>
              {Object.entries(filteredFilters.groups).map(
                ([label, items], groupIndex) => (
                  <Box key={groupIndex}>
                    <ListItemButton
                      key={label + groupIndex}
                      onClick={() => handleExpand(groupIndex)}
                      disableGutters
                      divider
                    >
                      <ListItemText
                        primary={
                          <Typography variant="buttonLg">{label}</Typography>
                        }
                      />
                      {expanded[groupIndex] ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                    <Collapse
                      in={expanded[groupIndex]}
                      timeout="auto"
                      unmountOnExit
                    >
                      {items.map((filter, itemIndex) => (
                        <Box key={itemIndex}>
                          <CollapseFilterItem
                            filter={filter}
                            query={drawerQuery}
                            setQuery={setDrawerQuery}
                            key={itemIndex}
                          />
                          {itemIndex !== items.length - 1 ? (
                            <Divider></Divider>
                          ) : null}
                        </Box>
                      ))}
                    </Collapse>
                  </Box>
                )
              )}

              {filteredFilters.items.map((filter) => (
                <Stack key={filter.name} spacing={0}>
                  <CollapseFilterItem
                    filter={filter}
                    query={drawerQuery}
                    setQuery={setDrawerQuery}
                    key={`${filter.name}-item`}
                  />
                  <Divider></Divider>
                </Stack>
              ))}
            </List>

            <Stack direction="row" spacing={1.6}>
              <Button
                variant="contained"
                size="large"
                onClick={handleFilterDrawerApply}
                sx={{ flex: 1, minHeight: '4.8rem' }}
              >
                Применить
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleFilterReset}
                sx={{ flex: 1, minHeight: '4.8rem' }}
              >
                Сбросить
              </Button>
            </Stack>
          </Stack>
        </Container>
      </Drawer>
    </>
  )
}

export default Filter
